import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { TextField, Button, FormControl, Select, MenuItem, Typography } from "@mui/material";
import { addDoc, collection, updateDoc, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { LEVELS, SUBJECTS } from "../constants";

const NewGroupForm = ({ onClose, setGroups, teachers }) => {
  const { t } = useTranslation();
  const [error, setError] = useState('');
  const [disabledButton, setDisabledButton] = useState(false);
  const [groupData, setGroupData] = useState({
    name: "",
    level: "",
    subjectId: "",
    teacherId: "",
  });

  // Fonction pour valider les données avant l'ajout
  const validateGroupData = () => {
    if (!groupData.name.trim()) {
      setError(t("Group name is required"));
      return false;
    }
    if (!groupData.level) {
      setError(t("Level is required"));
      return false;
    }
    if (!groupData.subjectId) {
      setError(t("Subject is required"));
      return false;
    }
    if (!groupData.teacherId) {
      setError(t("Teacher is required"));
      return false;
    }
    return true;
  };

  const handleAddGroup = async () => {
    // Validation avant d'ajouter un groupe
    if (!validateGroupData()) return;
    setDisabledButton(true)

    try {
      console.log("Validation réussie. Vérification de l'unicité du nom du groupe...");
  
      // Fetch all groups and check for duplicates with case-insensitive comparison
      const groupsSnapshot = await getDocs(collection(db, "groups"));
      const groupExists = groupsSnapshot.docs.some(
        (doc) =>
          doc.data().name.trim().toLowerCase() === groupData.name.trim().toLowerCase()
      );
  
      if (groupExists) {
        setError(t("A group with this name already exists. Please choose another name."));
        return;
      }
  
      console.log("Nom unique validé. Ajout du groupe en cours...");
  
      // Trouver les détails de la matière sélectionnée
      const selectedSubject = SUBJECTS.find((subject) => subject.id === groupData.subjectId);
  
      // Créer le nouveau groupe avec les détails supplémentaires
      const newGroup = {
        ...groupData,
        subjectName: selectedSubject?.name || "",
        subjectLevel: selectedSubject?.level || "",
        studentIds: [], // Liste vide d'étudiants par défaut
      };
  
      // Ajouter le groupe à Firestore
      const groupDocRef = await addDoc(collection(db, "groups"), newGroup);
      console.log("Groupe ajouté avec succès :", groupDocRef.id);
  
      // Récupérer l'enseignant sélectionné (groupData.teacherId est son ID document Firestore)
      const teacherDocRef = doc(db, "teachers", groupData.teacherId);
      const teacherSnapshot = await getDoc(teacherDocRef);

      if (!teacherSnapshot.exists()) {
        throw new Error("Enseignant introuvable dans Firestore.");
      }

      const existingGroupIds = teacherSnapshot.data().groupIds || [];
      console.log("GroupIds existants :", existingGroupIds);
  
      // Mettre à jour le champ groupIds avec le nouvel ID
      const updatedGroupIds = [...new Set([...existingGroupIds, groupDocRef.id])];
      console.log("GroupIds mis à jour :", updatedGroupIds);
  
      await updateDoc(teacherDocRef, {
        groupIds: updatedGroupIds,
      });
  
      console.log("GroupIds mis à jour avec succès pour l'enseignant :", groupData.teacherId);
  
      // Mettre à jour immédiatement le state local
      setGroups((prev) => [...prev, { id: groupDocRef.id, ...newGroup }]);
  
      // Réinitialiser les données du formulaire
      setGroupData({ name: "", level: "", subjectId: "", teacherId: "" });
      setDisabledButton(false)
      onClose();
    } catch (error) {
      console.error("Erreur lors de l'ajout du groupe :", error);
      setError(t("An error occurred while adding the group."));
    }
  };
  
  

  return (
    <div>
        <Typography sx={{color: 'red', display: 'flex', justifyContent: 'center'}}> {error} </Typography>
        <div>
          {/* Champ pour le nom du groupe */}
          <TextField
            label={t("Group name")}
            fullWidth
            margin="normal"
            value={groupData.name}
            onChange={(e) => setGroupData({ ...groupData, name: e.target.value })}
          />

          {/* Sélecteur pour le niveau */}
          <FormControl fullWidth margin="normal">
            <Typography variant="subtitle1">{t("Level")}</Typography>
            <Select
              value={groupData.level}
              onChange={(e) => setGroupData({ ...groupData, level: e.target.value })}
            >
              <MenuItem value="">{t("Select level")}</MenuItem>
              {LEVELS.map((level) => (
                <MenuItem key={level} value={level}>
                  {level}
                </MenuItem>
              ))}
            </Select>
          </FormControl>


          {/* Sélecteur pour la matière */}
          <FormControl fullWidth margin="normal">
            <Typography variant="subtitle1">{t("Subject")}</Typography>
            <Select
              value={groupData.subjectId}
              onChange={(e) => setGroupData({ ...groupData, subjectId: e.target.value })}
            >
              <MenuItem value="">{t("Select subject")}</MenuItem>
              {SUBJECTS.filter((subject) => subject.level === groupData.level).map((subject) => (
                <MenuItem key={subject.id} value={subject.id}>
                  {subject.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Sélecteur pour l'enseignant */}
          <FormControl fullWidth margin="normal">
            <Typography variant="subtitle1">{t("Teacher")}</Typography>
            <Select
              value={groupData.teacherId}
              onChange={(e) => setGroupData({ ...groupData, teacherId: e.target.value })}
            >
              <MenuItem value="">{t("Select teacher")}</MenuItem>
              {teachers
                .filter((teacher) => teacher.subjectIds.includes(groupData.subjectId))
                .map((teacher) => (
                  <MenuItem key={teacher.id} value={teacher.id}>
                    {`${teacher.firstName} ${teacher.lastName}`}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          
          {/* Bouton pour soumettre */}
          <Button
            variant="contained"
            fullWidth
            onClick={handleAddGroup}
            sx={{ marginTop: "20px", fontSize: '15px' }}
            disabled={disabledButton}
          >
           {t("Add group")}
          </Button>
        </div>
    </div>
  );
};

export default NewGroupForm;
